import React from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 TooltipProvider, Tooltip, TooltipTrigger, TooltipContent
} from 'shared/components/ui/tooltip';
import {
 Info
} from 'lucide-react';

interface ConnectionSettings {
    host?: string;
    port?: number;
    database?: string;
    username?: string;
    password?: string;
    driver?: string;
    encrypt?: boolean;
    ssl_mode?: 'disable' | 'require' | 'verify-ca';
    ssl_root_cert?: string;
    connection_timeout?: number;
}

interface RelationalDbFieldsProps {
    type: 'postgresql' | 'mysql' | 'sqlserver'; // Database type
    connection: { settings?: ConnectionSettings }; // Connection settings
}

const inputStyles = 'focus-visible:ring-neutral-950 dark:focus-visible:ring-neutral-300';

// Tooltip content
const tooltips = {
    host: 'The hostname or IP address of the database server.',
    port: 'The port number the database server is listening on.',
    database: 'The name of the database you want to connect to.',
    username: 'The username used to authenticate the database connection.',
    password: 'The password for the provided username.',
    driver: 'The database driver to use (only applicable for SQL Server).',
    encrypt: 'Specify whether the connection should be encrypted.',
    ssl_mode: 'Specify the SSL/TLS mode for the connection.',
    ssl_root_cert: 'Path or content of the SSL root certificate for verifying the server identity.',
    connection_timeout: 'The time (in seconds) to wait for a connection before timing out.',
};

export const RelationalDbFields: React.FC<RelationalDbFieldsProps> = ({
 type, connection
}) => {
    const settings = connection?.settings || {};

    const defaultPorts = {
        postgresql: 5432,
        mysql: 3306,
        sqlserver: 1433,
    };

    return (
        <TooltipProvider>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Host */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="host">
                                Host
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.host}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="host"
                        name="host"
                        required
                        placeholder="db.mycompany.com"
                        defaultValue={settings.host}
                        className={inputStyles}
                    />
                </div>

                {/* Port */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="port">
                                Port
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.port}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="port"
                        name="port"
                        type="number"
                        required
                        placeholder={String(defaultPorts[type])}
                        defaultValue={settings.port || defaultPorts[type]}
                        className={inputStyles}
                    />
                </div>

                {/* Database Name */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="database">
                                Database Name
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.database}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="database"
                        name="database"
                        required
                        placeholder="e.g., my_database"
                        defaultValue={settings.database}
                        className={inputStyles}
                    />
                </div>

                {/* Username */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="username">
                                Username
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.username}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="username"
                        name="username"
                        required
                        placeholder="e.g., admin"
                        defaultValue={settings.username}
                        className={inputStyles}
                    />
                </div>

                {/* Password */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="password">
                                Password
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.password}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="password"
                        name="password"
                        type="password"
                        required
                        defaultValue={settings.password}
                        className={inputStyles}
                    />
                </div>

                {/* SQL Server Specific Fields */}
                {type === 'sqlserver' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="driver">
                                        Driver
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.driver}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="driver"
                                name="driver"
                                placeholder="ODBC Driver 17 for SQL Server"
                                defaultValue={settings.driver || 'ODBC Driver 17 for SQL Server'}
                                className={inputStyles}
                            />
                        </div>

                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="encrypt">
                                        Encrypt Connection
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.encrypt}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Select name="encrypt" defaultValue={String(settings.encrypt || false)}>
                                <SelectTrigger className={inputStyles}>
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="true">True</SelectItem>
                                    <SelectItem value="false">False</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}

                {/* Non-SQL Server SSL/TLS Mode */}
                {type !== 'sqlserver' && (
                    <>
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="ssl_mode">
                                        SSL/TLS Mode
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.ssl_mode}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Select name="ssl_mode" defaultValue={settings.ssl_mode || 'disable'}>
                                <SelectTrigger className={inputStyles}>
                                    <SelectValue placeholder="Select mode" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="disable">Disable</SelectItem>
                                    <SelectItem value="require">Require</SelectItem>
                                    {type === 'postgresql' && <SelectItem value="verify-ca">Verify CA</SelectItem>}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* SSL Root Certificate */}
                        <div className="space-y-2">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Label htmlFor="ssl_root_cert">
                                        SSL Root Certificate (Optional)
                                        <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                                    </Label>
                                </TooltipTrigger>
                                <TooltipContent className="w-[300px]">
                                    <p>{tooltips.ssl_root_cert}</p>
                                </TooltipContent>
                            </Tooltip>
                            <Input
                                id="ssl_root_cert"
                                name="ssl_root_cert"
                                placeholder="/path/to/root-ca.pem"
                                defaultValue={settings.ssl_root_cert}
                                className={inputStyles}
                            />
                        </div>
                    </>
                )}

                {/* Connection Timeout */}
                <div className="space-y-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Label htmlFor="connection_timeout">
                                Connection Timeout (seconds)
                                <Info className="inline-block w-4 h-4 text-gray-500 ml-2 cursor-pointer hover:text-gray-800" />
                            </Label>
                        </TooltipTrigger>
                        <TooltipContent className="w-[300px]">
                            <p>{tooltips.connection_timeout}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Input
                        id="connection_timeout"
                        name="connection_timeout"
                        type="number"
                        placeholder="30"
                        defaultValue={settings.connection_timeout || 30}
                        className={inputStyles}
                    />
                </div>
            </div>
        </TooltipProvider>
    );
};
